import React, { useMemo, useRef, useState } from "react";
import type { ComponentProps } from "react";
import { GaraazPlaceEnquiryForm } from "@svan-garaaz/reactsharedlib";
import {
  Badge,
  Box,
  Button,
  Divider,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { FaMagic } from "react-icons/fa";
import Services from "./services";
import useNotification from "../../utils/useNotification";
import globalConstants from "../../utils/constants";
import PageScaffold from "../../components/PageScaffold";
import { useGetOrderMediaConfig } from "./queryHooks";
import {
  buildFormPrefillValuesFromDefaults,
  buildFormPrefillValuesFromOrder,
  buildTestPrefillDefaults,
  getOrderNoValidationError,
  getPreFillValidationError,
  hasResolvablePartDisplay,
  hydratePrefillDefaultsFromCustomer,
  mergeResolvedPartWithDefaults,
  type CustomerForPrefill,
  type TestPrefillDefaults,
} from "./testPrefillConfig";
import { ENQUIRY_SOURCE } from "../../constants/enquirySource";
import { generatePath } from "react-router-dom";
import "./PlaceOrderFormTest.css";

type GaraazPlaceEnquiryFormValues = Parameters<
  NonNullable<ComponentProps<typeof GaraazPlaceEnquiryForm>["onSubmit"]>
>[0];

/** Hide challan/extra columns Orca does not use on place enquiry. */
const PLACE_ENQUIRY_FIELDS: NonNullable<
  ComponentProps<typeof GaraazPlaceEnquiryForm>["fields"]
> = {
  workshopCode: { isVisible: false },
  associatedBranchName: { isVisible: false },
  mrp: { isVisible: false },
  dateOfPurchase: { isVisible: false },
  totalAmount: { isVisible: false },
};

const PlaceOrderFormTest: React.FC = () => {
  const [formKey, setFormKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTestOrder, setIsTestOrder] = useState(false);
  const [isPreFilling, setIsPreFilling] = useState(false);
  const [isPrefillingForm, setIsPrefillingForm] = useState(false);
  const [orderNoInput, setOrderNoInput] = useState("");
  const [isPrefillingFromOrder, setIsPrefillingFromOrder] = useState(false);
  const [prefilledFormValues, setPrefilledFormValues] = useState<
    Partial<GaraazPlaceEnquiryFormValues> | undefined
  >(undefined);
  const isTestOrderRef = useRef(false);
  const initiatorIdRef = useRef(
    JSON.parse(localStorage.getItem(globalConstants.ORCA_USER) ?? "{}")._id,
  );
  const toast = useNotification();
  const authToken = localStorage.getItem(globalConstants.AUTH_TOKEN) ?? "";
  const { data: mobileConfig, isLoading: isMobileConfigLoading } =
    useGetOrderMediaConfig();

  const typesenseConfig = useMemo(() => {
    const rawHost = mobileConfig?.typesense_host?.trim();
    const apiKey = mobileConfig?.typesense_api_key?.trim();
    if (!rawHost || !apiKey) return null;
    const host = rawHost.replace(/^https?:\/\//i, "").split("/")[0];
    if (!host) return null;
    return { host, apiKey };
  }, [mobileConfig?.typesense_host, mobileConfig?.typesense_api_key]);

  const toggleTestOrder = () => {
    setIsTestOrder((prev) => {
      isTestOrderRef.current = !prev;
      return !prev;
    });
  };

  const buildPayload = (
    values: GaraazPlaceEnquiryFormValues,
    isTestOrder: boolean,
  ) => {
    const user = JSON.parse(
      localStorage.getItem(globalConstants.ORCA_USER) ?? "{}",
    );
    const { insuranceClaimId, spocDetails, surveyorDetails, ...restBilling } =
      values.billingDetails ?? {};
    return {
      customerId: values.selectedCustomer._id,
      initiatorId: user._id,
      customerRemarks: values.remarks,
      shipmentAddress: values.customerAddress,
      orderDate: new Date(values.orderDateTime),
      orderItems: values.partsTableData.map((p) => ({
        partId: p._id,
        orderedQuantity: p.orderedQuantity,
      })),
      orderMedia:
        values.media.length > 0
          ? values.media.map((m) => m.mediaId)
          : undefined,
      carMaker: values.carMaker || undefined,
      licensePlateNumber: values.licensePlateNumber || undefined,
      carModel: values.carModel || undefined,
      vinNumber: values.vinNumber || undefined,
      billingDetails:
        Object.keys(restBilling).length > 0 ? restBilling : undefined,
      insuranceClaimId: insuranceClaimId || undefined,
      ...(spocDetails &&
      (spocDetails.name?.trim() ||
        spocDetails.mobileNumber?.trim() ||
        spocDetails.estimateApprovalFormUrl?.trim())
        ? { spocDetails }
        : {}),
      ...(surveyorDetails &&
      (surveyorDetails.name?.trim() || surveyorDetails.mobileNumber?.trim())
        ? { surveyorDetails }
        : {}),
      isTestOrder,
      source: ENQUIRY_SOURCE.WEB_ORCA,
    };
  };

  const handleSubmit = async (values: GaraazPlaceEnquiryFormValues) => {
    if (!values.selectedCustomer) {
      toast({ description: "Please select a customer.", status: "error" });
      return;
    }
    if (!values.customerAddress) {
      toast({
        description: "Please select a delivery address.",
        status: "error",
      });
      return;
    }
    if (!values.orderDateTime) {
      toast({
        description: "Please set an order date and time.",
        status: "error",
      });
      return;
    }
    if (!values.carMaker) {
      toast({ description: "Please select a car maker.", status: "error" });
      return;
    }
    const hasContent =
      (values.remarks ?? "").trim().length > 0 ||
      values.media.length > 0 ||
      values.partsTableData.length > 0;
    if (!hasContent) {
      toast({
        description: "Please add at least one part, media, or a remark.",
        status: "error",
      });
      return;
    }
    const hasZeroQty = values.partsTableData.some(
      (p) => p.orderedQuantity <= 0,
    );
    if (hasZeroQty) {
      toast({
        description: "All parts must have a quantity greater than zero.",
        status: "error",
      });
      return;
    }

    const orderTab = window.open("about:blank", "_blank");
    setIsSubmitting(true);
    try {
      const payload = buildPayload(values, isTestOrderRef.current);
      const order = await Services.placeGaraazOrder(payload);
      if (order) {
        toast({
          description: `Thank you! Your enquiry (${order.orderNo}) has been placed and is being processed.`,
          status: "success",
        });
        setFormKey((k) => k + 1);
        setPrefilledFormValues(undefined);
        setIsTestOrder(false);
        isTestOrderRef.current = false;
        const orderPath = generatePath("/orders/:orderNo", {
          orderNo: String(order.orderNo),
        });
        if (orderTab) {
          orderTab.location.assign(orderPath);
        } else {
          window.open(orderPath, "_blank");
        }
      } else {
        orderTab?.close();
        toast({
          description: "We're unable to place your enquiry. Please try again.",
          status: "error",
        });
      }
    } catch {
      orderTab?.close();
      toast({
        description: "We're unable to place your enquiry. Please try again.",
        status: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getResolvedPreFillDefaults = () =>
    buildTestPrefillDefaults(
      mobileConfig?.orca_test_order_prefill_config ?? null,
    );

  const resolvePreFillDefaults = async (): Promise<{
    defaults: TestPrefillDefaults;
    customer: CustomerForPrefill | null;
  }> => {
    const config = mobileConfig?.orca_test_order_prefill_config ?? null;
    if (!config?.customerId || !config?.partId) {
      return {
        defaults: buildTestPrefillDefaults(config),
        customer: null,
      };
    }

    let customer: CustomerForPrefill | null = null;
    try {
      customer = await Services.getCustomerForPrefill(config.customerId);
    } catch {
      customer = null;
    }

    if (customer) {
      return {
        defaults: hydratePrefillDefaultsFromCustomer(config, customer),
        customer,
      };
    }

    return {
      defaults: buildTestPrefillDefaults(config),
      customer: null,
    };
  };

  const resolvePartForPreFill = async (defaults: TestPrefillDefaults) => {
    let resolvedPart = mergeResolvedPartWithDefaults(defaults);
    try {
      const apiPart = await Services.resolvePartForPrefill(defaults.partId, {
        partNumber: defaults.partNumber,
        partName: defaults.partName,
        carMaker: defaults.carMaker,
      });
      resolvedPart = mergeResolvedPartWithDefaults(defaults, apiPart);
    } catch {
      resolvedPart = mergeResolvedPartWithDefaults(defaults);
    }
    return resolvedPart;
  };

  const handlePrefillFromOrderNo = async () => {
    const validationError = getOrderNoValidationError(orderNoInput);
    if (validationError) {
      toast({ description: validationError, status: "error" });
      return;
    }

    setIsPrefillingFromOrder(true);
    try {
      const order = await Services.getOrderByOrderNo(orderNoInput);
      if (!order) {
        toast({
          description: "No order found for this order number.",
          status: "error",
        });
        return;
      }

      const customerId =
        order.customer?._id != null
          ? String(order.customer._id)
          : order.customerId != null
            ? String(order.customerId)
            : "";

      let customer: CustomerForPrefill | null = order.customer ?? null;
      if (customerId) {
        try {
          const fetched = await Services.getCustomerForPrefill(customerId);
          if (fetched) customer = fetched;
        } catch {
          // Keep order.customer when customer fetch fails.
        }
      }

      let carMakersData: Array<{ name?: string; makerName?: string }> = [];
      try {
        const makers = await Services.getCarMakers();
        carMakersData = Array.isArray(makers) ? makers : [];
      } catch {
        carMakersData = [];
      }

      const prefilled = buildFormPrefillValuesFromOrder(order, {
        customer,
        carMakersData,
        // Use the source order date; clamped so datetime-local max accepts it.
        useCurrentDateTime: false,
      });

      if (!prefilled) {
        toast({
          description:
            "Order is missing customer or delivery address details needed to fill the form.",
          status: "error",
        });
        return;
      }

      setPrefilledFormValues(prefilled);
      setFormKey((key) => key + 1);
      if (order.isTestOrder) {
        setIsTestOrder(true);
        isTestOrderRef.current = true;
      }
      toast({
        description: `Form filled from order ${order.orderNo}. Review before submitting.`,
        status: "success",
      });
    } catch {
      toast({
        description: "Could not fetch order details. Please try again.",
        status: "error",
      });
    } finally {
      setIsPrefillingFromOrder(false);
    }
  };

  const handlePreFillForm = async () => {
    setIsPrefillingForm(true);
    const { defaults, customer } = await resolvePreFillDefaults();
    const validationError = getPreFillValidationError(defaults);

    if (validationError) {
      toast({ description: validationError, status: "error" });
      setIsPrefillingForm(false);
      return;
    }

    const resolvedPart = await resolvePartForPreFill(defaults);

    if (!hasResolvablePartDisplay(resolvedPart)) {
      toast({
        description:
          "Could not load part details for pre-fill. Add partNumber and partName to orca_test_order_prefill_config.",
        status: "error",
      });
      setIsPrefillingForm(false);
      return;
    }

    setPrefilledFormValues(
      buildFormPrefillValuesFromDefaults(defaults, resolvedPart, {
        useCurrentDateTime: true,
        customer,
      }),
    );
    setFormKey((key) => key + 1);
    toast({
      description: customer
        ? "Customer and part details added. Review the form before submitting."
        : "Default test details added. Review the form before submitting.",
      status: "success",
    });
    setIsPrefillingForm(false);
  };

  const handlePreFillAndPlace = async () => {
    setIsPreFilling(true);
    const { defaults } = await resolvePreFillDefaults();
    const validationError = getPreFillValidationError(defaults);

    if (validationError) {
      toast({ description: validationError, status: "error" });
      setIsPreFilling(false);
      return;
    }

    const payload = {
      customerId: defaults.customerId,
      initiatorId: initiatorIdRef.current,
      customerRemarks: defaults.remarks,
      shipmentAddress: defaults.shipmentAddress,
      billingDetails: defaults.billingDetails,
      orderDate: new Date(),
      orderItems: [{ partId: defaults.partId, orderedQuantity: 1 }],
      carMaker: defaults.carMaker,
      isTestOrder: true,
      source: ENQUIRY_SOURCE.WEB_ORCA,
    };

    try {
      const order = await Services.placeGaraazOrder(payload);
      if (order) {
        toast({
          description: `Thank you! Your enquiry (${order.orderNo}) has been placed and is being processed.`,
          status: "success",
        });
      } else {
        toast({
          description: "Could not place enquiry. Please try again.",
          status: "error",
        });
      }
    } catch {
      toast({
        description: "Could not place enquiry. Please try again.",
        status: "error",
      });
    } finally {
      setIsPreFilling(false);
    }
  };

  return (
    <PageScaffold title="Place Enquiry" isHeaderVisible={false}>
      <div className="orca-place-enquiry-form">
        {/* ── Action bar ── */}
        <Box
          mb={4}
          p={3}
          borderRadius="lg"
          bg={isTestOrder ? "orange.50" : "gray.50"}
          border="1px solid"
          borderColor={isTestOrder ? "orange.200" : "gray.200"}
          transition="all 0.2s"
        >
          <HStack
            justify="space-between"
            align="center"
            flexWrap="wrap"
            gap={3}
          >
            <HStack spacing={3} align="center">
              <Text fontSize="sm" fontWeight="medium" color="gray.600">
                Order Mode:
              </Text>
              <Tooltip
                label={
                  isTestOrder
                    ? "This order will be marked as a test order"
                    : "Click to mark this as a test order"
                }
                placement="top"
                hasArrow
              >
                <Button
                  size="sm"
                  variant={isTestOrder ? "solid" : "outline"}
                  colorScheme={isTestOrder ? "orange" : "gray"}
                  onClick={toggleTestOrder}
                  leftIcon={
                    <Text as="span" fontSize="14px">
                      {isTestOrder ? "🧪" : "📋"}
                    </Text>
                  }
                  borderRadius="full"
                  px={4}
                  fontWeight="semibold"
                >
                  {isTestOrder ? "Test Order: ON" : "Test Order: OFF"}
                </Button>
              </Tooltip>
              {isTestOrder && (
                <Badge
                  colorScheme="orange"
                  variant="subtle"
                  fontSize="0.7rem"
                  px={2}
                  py={1}
                  borderRadius="md"
                >
                  ORDER WILL BE FLAGGED AS TEST
                </Badge>
              )}
            </HStack>

            <Divider
              orientation="vertical"
              height="28px"
              display={{ base: "none", md: "block" }}
            />

            <HStack spacing={2} flexShrink={0} flexWrap="nowrap" align="center">
              <InputGroup size="sm" w="220px" flexShrink={0}>
                <Input
                  placeholder="Order/Enquiry Number"
                  value={orderNoInput}
                  onChange={(e) => setOrderNoInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      void handlePrefillFromOrderNo();
                    }
                  }}
                  bg="white"
                  borderRadius="full"
                  pr="2.75rem"
                  isDisabled={isPrefillingFromOrder}
                />
                <InputRightElement width="2.5rem">
                  <Tooltip
                    label="Auto-fill enquiry form from order/enquiry number"
                    placement="top"
                    hasArrow
                  >
                    <IconButton
                      aria-label="Auto-fill from order number"
                      icon={<FaMagic />}
                      size="xs"
                      variant="ghost"
                      colorScheme="blue"
                      borderRadius="full"
                      onClick={() => void handlePrefillFromOrderNo()}
                      isLoading={isPrefillingFromOrder}
                      isDisabled={isPrefillingFromOrder || !orderNoInput.trim()}
                    />
                  </Tooltip>
                </InputRightElement>
              </InputGroup>

              <Tooltip label="Fill the enquiry form" placement="top" hasArrow>
                <Button
                  size="sm"
                  colorScheme="blue"
                  variant="outline"
                  onClick={handlePreFillForm}
                  isLoading={isPrefillingForm || isMobileConfigLoading}
                  isDisabled={isMobileConfigLoading}
                  loadingText="Filling…"
                  leftIcon={
                    <Text as="span" fontSize="14px">
                      📝
                    </Text>
                  }
                  borderRadius="full"
                  px={4}
                  fontWeight="semibold"
                  flexShrink={0}
                  whiteSpace="nowrap"
                  _hover={{ bg: "blue.50" }}
                >
                  Auto Fill
                </Button>
              </Tooltip>

              <Tooltip
                label="Auto-fill and place a test order in one click"
                placement="top"
                hasArrow
              >
                <Button
                  size="sm"
                  colorScheme="purple"
                  variant="outline"
                  onClick={handlePreFillAndPlace}
                  isLoading={isPreFilling || isMobileConfigLoading}
                  isDisabled={isMobileConfigLoading}
                  loadingText="Placing…"
                  leftIcon={
                    <Text as="span" fontSize="14px">
                      ⚡
                    </Text>
                  }
                  borderRadius="full"
                  px={4}
                  fontWeight="semibold"
                  flexShrink={0}
                  whiteSpace="nowrap"
                  _hover={{ bg: "purple.50" }}
                >
                  Auto Fill &amp; Place
                </Button>
              </Tooltip>
            </HStack>
          </HStack>
        </Box>

        <GaraazPlaceEnquiryForm
          key={formKey}
          authToken={authToken}
          apiBaseUrl={process.env.REACT_APP_BASE_URL ?? ""}
          typesenseConfig={typesenseConfig}
          addCountryCodeToSearch
          fields={PLACE_ENQUIRY_FIELDS}
          prefilledValues={prefilledFormValues ?? {}}
          isSubmitting={isSubmitting}
          onSubmit={handleSubmit}
          onNotification={toast}
          useSharedPartsSearch
          enablePartSearchHighlighting
        />
      </div>
    </PageScaffold>
  );
};

export default PlaceOrderFormTest;
